import { ExtendedTicket, TicketCategory, TicketStatus } from '../../types';
import {
  Nis2EarlyWarningData, Nis2EntityProfile, Nis2FinalData, Nis2IntermediateData,
  Nis2NotificationData, Nis2Report, Nis2Significance, Nis2Stage, Nis2StageDataMap,
  Nis2StageReport, Nis2ThreatCategory,
} from '../../types/nis2';

// ─── Stage metadata ──────────────────────────────────────────────────────────

export const STAGE_META: Record<Nis2Stage, { title: string; tab: string; article: string; purpose: string }> = {
  early_warning: {
    title: 'Early Warning',
    tab: 'Early warning · 24h',
    article: 'Art. 23(4)(a)',
    purpose: 'Rapid notice to the CSIRT / competent authority, indicating whether the incident is suspected '
      + 'to be caused by unlawful or malicious acts and whether it could have a cross-border impact.',
  },
  notification: {
    title: 'Incident Notification',
    tab: 'Notification · 72h',
    article: 'Art. 23(4)(b)',
    purpose: 'Updates the early warning with an initial assessment of the incident, including its severity '
      + 'and impact, and indicators of compromise where available.',
  },
  intermediate: {
    title: 'Intermediate Report',
    tab: 'Intermediate',
    article: 'Art. 23(4)(c)',
    purpose: 'Relevant status updates, submitted only upon request of the CSIRT or competent authority.',
  },
  final: {
    title: 'Final Report',
    tab: 'Final / Progress',
    article: 'Art. 23(4)(d)–(e)',
    purpose: 'Detailed description, root cause, applied and ongoing mitigation, cross-border impact. '
      + 'If the incident is still ongoing, submit a progress report instead; the final report is then due '
      + 'within one month of the incident being handled.',
  },
};

export const finalReportTitle = (data: Partial<Nis2FinalData>) =>
  data.is_incident_resolved === false ? 'Progress Report' : 'Final Report';

export const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export const THREAT_CATEGORY_OPTIONS: { value: Nis2ThreatCategory; label: string }[] = [
  { value: 'ransomware', label: 'Ransomware' },
  { value: 'malware', label: 'Malware (other)' },
  { value: 'exploit', label: 'Vulnerability exploitation' },
  { value: 'denial_of_service', label: 'Denial of service' },
  { value: 'supply_chain', label: 'Supply chain compromise' },
  { value: 'phishing', label: 'Phishing / social engineering' },
  { value: 'unauthorized_access', label: 'Unauthorized access' },
  { value: 'data_exfiltration', label: 'Data exfiltration' },
  { value: 'insider', label: 'Insider threat' },
  { value: 'misconfiguration', label: 'Misconfiguration / human error' },
  { value: 'physical', label: 'Physical / environmental' },
  { value: 'other', label: 'Other' },
];

export const labelFor = (options: { value: string; label: string }[], value?: string) =>
  options.find(o => o.value === value)?.label || value || '';

const CATEGORY_LABELS: Record<TicketCategory, string> = {
  [TicketCategory.MALWARE]: 'Malware / Ransomware',
  [TicketCategory.PHISHING]: 'Phishing',
  [TicketCategory.UNAUTHORIZED_ACCESS]: 'Unauthorized access',
  [TicketCategory.DATA_BREACH]: 'Data breach',
  [TicketCategory.DENIAL_OF_SERVICE]: 'Denial of service',
  [TicketCategory.INSIDER_THREAT]: 'Insider threat',
  [TicketCategory.POLICY_VIOLATION]: 'Policy violation',
  [TicketCategory.SUSPICIOUS_ACTIVITY]: 'Suspicious activity',
  [TicketCategory.OTHER]: 'Other',
};

const CATEGORY_TO_THREAT: Record<TicketCategory, Nis2ThreatCategory> = {
  [TicketCategory.MALWARE]: 'malware',
  [TicketCategory.PHISHING]: 'phishing',
  [TicketCategory.UNAUTHORIZED_ACCESS]: 'unauthorized_access',
  [TicketCategory.DATA_BREACH]: 'data_exfiltration',
  [TicketCategory.DENIAL_OF_SERVICE]: 'denial_of_service',
  [TicketCategory.INSIDER_THREAT]: 'insider',
  [TicketCategory.POLICY_VIOLATION]: 'other',
  [TicketCategory.SUSPICIOUS_ACTIVITY]: 'other',
  [TicketCategory.OTHER]: 'other',
};

const MALICIOUS_CATEGORIES = new Set([
  TicketCategory.MALWARE,
  TicketCategory.PHISHING,
  TicketCategory.UNAUTHORIZED_ACCESS,
  TicketCategory.DATA_BREACH,
  TicketCategory.INSIDER_THREAT,
  TicketCategory.DENIAL_OF_SERVICE,
]);

// ─── Date helpers ────────────────────────────────────────────────────────────

/** ISO string → value for <input type="datetime-local"> in the viewer's timezone. */
export const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** datetime-local value → UTC ISO string (or null). */
export const fromLocalInput = (local: string): string | null => {
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

export const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
};

export const formatRemaining = (dueIso: string, now: number): { text: string; overdue: boolean; urgent: boolean } => {
  const diff = new Date(dueIso).getTime() - now;
  const abs = Math.abs(diff);
  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  const span = days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes}m`;
  return {
    text: diff >= 0 ? `due in ${span}` : `overdue by ${span}`,
    overdue: diff < 0,
    urgent: diff >= 0 && diff < 6 * 3_600_000,
  };
};

// ─── Report lookups ──────────────────────────────────────────────────────────

export const stageReports = (report: Nis2Report | null, stage: Nis2Stage): Nis2StageReport[] =>
  (report?.notifications || []).filter(n => n.stage === stage);

export const draftFor = (report: Nis2Report | null, stage: Nis2Stage) =>
  stageReports(report, stage).find(n => n.status === 'draft') || null;

export const latestSubmitted = (report: Nis2Report | null, stage: Nis2Stage) =>
  stageReports(report, stage)
    .filter(n => n.status === 'submitted' && n.submitted_at)
    .sort((a, b) => (b.submitted_at! > a.submitted_at! ? 1 : -1))[0] || null;

// ─── Defaults / pre-fill ─────────────────────────────────────────────────────

export const buildEntityDefaults = (report: Nis2Report | null): Nis2EntityProfile => ({
  entity_name: report?.ticket_context.client_name || '',
  registration_id: '',
  entity_type: '',
  sector: '',
  member_state: '',
  authority: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  cir_applicable: false,
  trust_service_provider: false,
  ...(report?.assessment.entity || {}),
});

export const buildSignificanceDefaults = (report: Nis2Report | null): Nis2Significance => ({
  severe_disruption_or_loss: false,
  considerable_damage_to_others: false,
  financial_loss_eur: null,
  annual_turnover_eur: null,
  trade_secret_exfiltration: false,
  death: false,
  considerable_health_damage: false,
  malicious_unauthorised_access: false,
  recurring: false,
  entity_specific_threshold_met: false,
  entity_specific_notes: '',
  affected_users: null,
  duration_hours: null,
  decision: 'undetermined',
  decision_rationale: '',
  ...(report?.assessment.significance || {}),
});

const affectedServices = (ticket: ExtendedTicket): string => {
  const items = [
    ticket.affected_asset
      ? `${ticket.affected_asset.hostname}${ticket.affected_asset.asset_type ? ` (${ticket.affected_asset.asset_type})` : ''}`
      : null,
    ...(ticket.impact_remediation?.affected_systems || []),
  ].filter(Boolean) as string[];
  return Array.from(new Set(items)).join(', ');
};

const actionsTaken = (ticket: ExtendedTicket): string =>
  ticket.impact_remediation?.actions_taken?.map(a => `• ${a.action}`).join('\n') || '';

const fromTicket = (ticket: ExtendedTicket, report: Nis2Report | null): Nis2StageDataMap => {
  const category = ticket.meta?.category;
  const ew = (latestSubmitted(report, 'early_warning')?.data || {}) as Partial<Nis2EarlyWarningData>;
  const notif = (latestSubmitted(report, 'notification')?.data || {}) as Partial<Nis2NotificationData>;
  const resolved = [TicketStatus.RESOLVED, TicketStatus.CLOSED].includes(ticket.meta?.status);

  const earlyWarning: Nis2EarlyWarningData = {
    detection_datetime: ticket.alert?.detection_timestamp || '',
    incident_type: CATEGORY_LABELS[category] || category || '',
    affected_services: affectedServices(ticket),
    initial_summary: [ticket.title, ticket.description].filter(Boolean).join('\n'),
    is_malicious_or_unlawful: MALICIOUS_CATEGORIES.has(category),
    suspected_cross_border_impact: false,
    affected_member_states: [],
    containment_initiated: (ticket.impact_remediation?.actions_taken?.length || 0) > 0,
    measures_taken: actionsTaken(ticket),
    assistance_requested: false,
    assistance_details: '',
  };

  const notification: Nis2NotificationData = {
    initial_assessment: ticket.impact_remediation?.estimated_business_impact || ew.initial_summary || earlyWarning.initial_summary,
    severity: ticket.meta?.priority || '',
    threat_category: CATEGORY_TO_THREAT[category] || '',
    affected_users_count: null,
    downtime_minutes: null,
    estimated_financial_loss_eur: null,
    is_malicious_or_unlawful: ew.is_malicious_or_unlawful ?? earlyWarning.is_malicious_or_unlawful,
    suspected_cross_border_impact: ew.suspected_cross_border_impact ?? false,
    affected_member_states: ew.affected_member_states || [],
    indicators_of_compromise: (ticket.iocs || []).map(i => ({ type: i.type, value: i.value })),
    gdpr_breach_triggered: !!ticket.impact_remediation?.data_exposure_confirmed,
    measures_taken: ew.measures_taken || earlyWarning.measures_taken,
  };

  const intermediate: Nis2IntermediateData = {
    requested_at: '',
    request_reference: '',
    current_status: resolved ? 'resolved' : '',
    status_update: '',
  };

  const mitre = (ticket.mitre_mappings || []).map(m => m.sub_technique_id || m.technique_id).filter(Boolean);
  const final: Nis2FinalData = {
    is_incident_resolved: resolved,
    handling_concluded_at: resolved ? (ticket.impact_remediation?.resolved_at || report?.ticket_context.resolved_at || '') : '',
    detailed_description: notif.initial_assessment || earlyWarning.initial_summary,
    severity: notif.severity || notification.severity,
    impact_summary: ticket.impact_remediation?.estimated_business_impact || '',
    root_cause_analysis: report?.ticket_context.root_cause || '',
    mitre_attack_techniques: Array.from(new Set(mitre)),
    mitigations_implemented: actionsTaken(ticket),
    mitigations_ongoing: (ticket.impact_remediation?.recommended_actions || [])
      .filter(a => !a.is_completed).map(a => `• ${a.action}`).join('\n'),
    cross_border_impact: (notif.suspected_cross_border_impact && notif.affected_member_states?.length)
      ? `Member States affected: ${notif.affected_member_states.join(', ')}` : '',
    lessons_learned: ticket.resolution_summary || '',
  };

  return { early_warning: earlyWarning, notification, intermediate, final };
};

/**
 * Form defaults for a stage: the open draft if any, otherwise the last submitted
 * report of that stage (e.g. a final report following a progress report),
 * otherwise values derived from the ticket and earlier stages.
 */
export function buildStageDefaults<S extends Nis2Stage>(
  stage: S, ticket: ExtendedTicket, report: Nis2Report | null,
): Nis2StageDataMap[S] {
  const base = fromTicket(ticket, report)[stage];
  const saved = draftFor(report, stage) || (stage === 'intermediate' ? null : latestSubmitted(report, stage));
  return { ...base, ...(saved?.data || {}) } as Nis2StageDataMap[S];
}

// ─── Advisory significance evaluation ────────────────────────────────────────

export const CIR_FINANCIAL_ABSOLUTE_EUR = 500_000;

/** CIR 2024/2690 Art. 3(1)(a): loss exceeding €500k or 5% of turnover, whichever is lower. */
export const financialThreshold = (turnover: number | null | undefined): number =>
  turnover && turnover > 0 ? Math.min(CIR_FINANCIAL_ABSOLUTE_EUR, turnover * 0.05) : CIR_FINANCIAL_ABSOLUTE_EUR;

/** Criteria that appear to be met. Advisory only — the decision stays with the analyst. */
export const indicatedCriteria = (s: Nis2Significance): string[] => {
  const out: string[] = [];
  if (s.severe_disruption_or_loss) out.push('Severe operational disruption or financial loss (Art. 23(3)(a))');
  if (s.considerable_damage_to_others) out.push('Considerable damage to other persons (Art. 23(3)(b))');
  if (s.financial_loss_eur != null && s.financial_loss_eur > financialThreshold(s.annual_turnover_eur)) {
    out.push('Financial loss above the CIR threshold (CIR Art. 3(1)(a))');
  }
  if (s.trade_secret_exfiltration) out.push('Exfiltration of trade secrets (CIR Art. 3(1)(b))');
  if (s.death) out.push('Death of a natural person (CIR Art. 3(1)(c))');
  if (s.considerable_health_damage) out.push('Considerable damage to health (CIR Art. 3(1)(d))');
  if (s.malicious_unauthorised_access) out.push('Suspected malicious unauthorised access capable of severe disruption (CIR Art. 3(1)(e))');
  if (s.recurring) out.push('Recurring incidents together exceeding the financial-loss threshold (CIR Art. 4)');
  if (s.entity_specific_threshold_met) out.push('Entity-specific or national threshold met');
  return out;
};
